import React from "react"
import { useTranslation } from "react-i18next"

import { useAuth } from "@treefort/lib/auth-provider"
import icons from "@treefort/tokens/app/icons"

import config from "../config"
import { useActiveProfileId } from "../hooks/use-active-profile-id"
import { useProgressItemForConsumableContent } from "../hooks/use-progress"
import { usePromptCreateFreeAccount } from "../hooks/use-prompt-create-free-account"
import { AppManifest } from "../lib/app-manifest"
import confirm from "../lib/confirm"
import {
  ConsumableContent,
  isConsumableContentAvailable,
} from "../lib/consumable-content"
import { stopAudioPlayerIfCurrent } from "../lib/content-audio"
import DownloadItem from "../lib/download-item"
import { kebabCase } from "../lib/kebab-case"
import {
  clearProgress,
  fetchProgress,
  fetchProgressForConsumableContent,
  getProgressItemKeyFromConsumableContent,
  persistProgress,
} from "../lib/progress-item"
import { toast } from "../lib/toaster"
import { isLibraryTab } from "../navigation/routes"
import AsyncIconButton from "./async-icon-button"
import IconButton from "./icon-button"

function AddToLibraryButtonAuthenticated({
  height,
  consumableContent,
}: {
  consumableContent: ConsumableContent | undefined
  height?: number
}) {
  const { t } = useTranslation()
  const profileId = useActiveProfileId()
  const progressItem = useProgressItemForConsumableContent({
    consumableContent,
    shouldUpdate: (prev, next) => Boolean(prev) !== Boolean(next),
  })
  const addedToLibrary = Boolean(progressItem)

  async function addToLibrary() {
    if (consumableContent) {
      persistProgress(
        await fetchProgressForConsumableContent({
          consumableContent,
          profileId,
        }),
      )
      toast.success(t("Added to your library!"))
    }
  }

  async function removeFromLibrary() {
    if (consumableContent) {
      const confirmRemove = await confirm({
        title: t("Remove from library?"),
        message: `${t(
          "Do you want to remove {{contentTitle}} from your library?",
          { contentTitle: consumableContent.content.title },
        )} ${t(
          config.DOWNLOADS_SUPPORTED
            ? "This will clear your saved progress and delete any downloaded data."
            : "This will clear your saved progress.",
        )}`,
        confirmLabel: t("Remove"),
        cancelLabel: t("Keep"),
        danger: true,
      })
      if (confirmRemove) {
        const progressItem = await fetchProgress({
          key: getProgressItemKeyFromConsumableContent(consumableContent),
          profileId,
        })
        if (progressItem) {
          await stopAudioPlayerIfCurrent(progressItem)
          clearProgress(progressItem)
        }
        await new DownloadItem({ consumableContent }).deleteDownload()
      }
    }
  }

  return (
    <AsyncIconButton
      id={kebabCase(
        `add-to-library-authenticated-${consumableContent?.content.id}-${consumableContent?.content.title || "unknown-title"}`,
      )}
      source={addedToLibrary ? icons.plusCircle.fill : icons.plusCircle.outline}
      label={addedToLibrary ? t("Remove from library") : t("Add to library")}
      color="secondary"
      onPress={addedToLibrary ? removeFromLibrary : addToLibrary}
      minSize="buttonHeight"
      height={height}
    />
  )
}

function AddToLibraryButtonUnauthenticated({
  consumableContent,
  height,
}: {
  height?: number
  consumableContent?: ConsumableContent
}): React.JSX.Element {
  const { t } = useTranslation()
  const promptCreateFreeAccount = usePromptCreateFreeAccount()

  return (
    <IconButton
      id={kebabCase(
        `add-to-library-unauthenticated-${consumableContent?.content.id}-${consumableContent?.content.title || "unknown-title"}`,
      )}
      source={icons.plusCircle.outline}
      label={t("Add to library")}
      color="secondary"
      onPress={() =>
        promptCreateFreeAccount(
          "You must have an account to add content to your library. Would you like to create one now?",
        )
      }
      minSize="buttonHeight"
      height={height}
    />
  )
}

export default function AddToLibraryButton({
  height,
  consumableContent,
}: {
  consumableContent?: ConsumableContent
  height?: number
}): React.JSX.Element {
  const auth = useAuth()
  return auth.user ? (
    <AddToLibraryButtonAuthenticated
      consumableContent={consumableContent}
      height={height}
    />
  ) : (
    <AddToLibraryButtonUnauthenticated
      consumableContent={consumableContent}
      height={height}
    />
  )
}

export function canAddToLibrary({
  manifest,
  consumableContent,
}: {
  manifest: AppManifest
  consumableContent?: ConsumableContent
}) {
  return (
    manifest.tabs.some(isLibraryTab) &&
    isConsumableContentAvailable(consumableContent)
  )
}
